import { ChangeDetectionStrategy, Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { underlayImageParam } from '../../../common';
import { MpxResolverService } from '../../../mpx';
import { PlayButtonTextPipe } from '../../pipes/play-button-text.pipe';
import { WatchFreeTrialPipe } from '../../pipes/watch-free-trial.pipe';
import { AbstractPlayButtonSpinnerService, ImgQuery, Screen, ScreenService, SimpleBookmarkItem } from '../../services';
import { AbstractNavigationService } from '../../services/abstract-navigation.service';
import { OsnComponentBase } from '../../utils';
import { AbstractMyListButtonService } from '../my-list-button/abstract-my-list.service';
import { HeroItemActionClickType, HeroItemViewModel } from './hero-item.view-model';

@Component({
  selector: 'osn-shared-hero-item',
  templateUrl: './hero-item.component.html',
  styleUrls: ['./hero-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroItemComponent extends OsnComponentBase implements OnInit {
  @Input() public vm: HeroItemViewModel;
  @Input() public readonlyMode = false; // readonlyMode is for cxb preview feature
  @Output() public heroItemButtonClick = new EventEmitter<{
    buttonText: string;
    heroItem: HeroItemViewModel;
  }>();
  @Output() public heroItemGenreClick = new EventEmitter<{ genreName: string; heroItem: HeroItemViewModel }>();
  public isMobile$: Observable<boolean>;
  public readonly HeroItemActionClickType = HeroItemActionClickType;
  public imgQuery: ImgQuery;
  public titleImgQuery: ImgQuery;
  public underlayImageParam = underlayImageParam;
  public readonly shouldShowPlayButtonSpinner$ = this.playButtonSpinnerService.showLoadingSpinner$;

  constructor(
    injector: Injector,
    private readonly screenService: ScreenService,
    private readonly navigation: AbstractNavigationService,
    private readonly playButtonTextPipe: PlayButtonTextPipe,
    private readonly watchFreeTrialPipe: WatchFreeTrialPipe,
    private readonly myListButtonService: AbstractMyListButtonService,
    private readonly mpxService: MpxResolverService,
    private readonly playButtonSpinnerService: AbstractPlayButtonSpinnerService
  ) {
    super(injector);
  }

  public ngOnInit() {
    this.isMobile$ = this.screenService.isXS$;
    this.imgQuery = {
      XS: { url: this.getPlatformImageUrl(Screen.XS) },
      SM: { url: this.getPlatformImageUrl(Screen.SM) },
      MD: { url: this.getPlatformImageUrl(Screen.MD) },
      LG: { url: this.getPlatformImageUrl(Screen.LG) },
      XL: { url: this.getPlatformImageUrl(Screen.XL) },
    };
    this.titleImgQuery = {
      XS: 320,
      SM: 768,
      MD: 378,
      LG: 504,
      XL: 630,
    };
  }

  public getLatestEpisode(bookmark: SimpleBookmarkItem): string {
    if (bookmark.seasonNumber && bookmark.episodeNumber) {
      return `S${bookmark.seasonNumber}:E${bookmark.episodeNumber} - ${bookmark.title[this.currentLanguage]}`;
    }
  }

  public onHeroItemActionClick(actionType: HeroItemActionClickType, hasProgress: boolean) {
    this.myListButtonService
      .isAddedToMyList$(this.vm.content)
      .pipe(take(1))
      .subscribe((isAddedToMyList) => {
        let buttonText: string;
        if (actionType === HeroItemActionClickType.MyListClick) {
          buttonText = isAddedToMyList ? 'Remove from my list' : 'Add to my list';
        } else if (actionType === HeroItemActionClickType.PlayClick) {
          buttonText = hasProgress ? 'Resume' : 'Play';
        } else {
          buttonText = 'More Info';
        }
        this.heroItemButtonClick.emit({
          buttonText,
          heroItem: this.vm,
        });
      });
  }

  public onGenreClick(genreName: string): void {
    this.heroItemGenreClick.emit({ genreName, heroItem: this.vm });
  }

  private getPlatformImageUrl(screen: Screen): string {
    const background = this.vm.backgroundImageUrlsByPlatform.find(
      (platformGroup) => platformGroup.platform === this.screenService.mapScreenToPlatformType(screen)
    );

    return background.imageUrl;
  }
}
